/**
 * Comparison Activity Details Component
 *
 * Displays grouped comparison details with VFX warnings and impact analysis
 */

import { useEffect, useState, useMemo } from 'react'
import { getComparison } from '@/lib/api/commits'
import type { ActivityDetailsResponse } from '@/lib/api/commits'
import type { Clip } from '@/types/database'
import { AlertTriangle, TrendingUp, TrendingDown, Minus, Plus, X, ArrowRight } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useClips } from '@/hooks/useClips'
import { useVfxShots } from '@/hooks/useVfxShots'

// Helper function to convert frames to timecode (HH:MM:SS:FF)
function framesToTimecode(frames: number, fps: number = 23.976): string {
  const isDropFrame = Math.abs(fps - 29.97) < 0.01 || Math.abs(fps - 59.94) < 0.01
  const roundedFps = Math.round(fps)

  let totalFrames = Math.floor(frames)

  // Handle drop-frame timecode for 29.97 and 59.94
  if (isDropFrame) {
    // Drop-frame calculation (drops 2 frames per minute except every 10th minute)
    const framesPerMinute = roundedFps * 60
    const framesPer10Minutes = framesPerMinute * 10

    const d = Math.floor(totalFrames / framesPer10Minutes)
    const m = Math.floor((totalFrames % framesPer10Minutes) / framesPerMinute)

    totalFrames += 18 * d + 2 * Math.max(m - 1, 0)
  }

  const ff = totalFrames % roundedFps
  const ss = Math.floor(totalFrames / roundedFps) % 60
  const mm = Math.floor(totalFrames / (roundedFps * 60)) % 60
  const hh = Math.floor(totalFrames / (roundedFps * 60 * 60))

  const separator = isDropFrame ? ';' : ':'
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}${separator}${String(ff).padStart(2, '0')}`
}

interface ComparisonActivityDetailsProps {
  comparisonId: string
  projectId: string
  activityMetadata?: any  // Activity log metadata that includes clips array with clip_id
  onViewClip?: (clipId: string) => void  // Handler to open clip details in sidebar
}

export function ComparisonActivityDetails({ comparisonId, projectId, activityMetadata, onViewClip }: ComparisonActivityDetailsProps) {
  const [activityDetails, setActivityDetails] = useState<ActivityDetailsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [sortBy, setSortBy] = useState<'default' | 'tag' | 'name' | 'track'>('default')

  // Fetch clips and VFX shots to get tag information
  const { data: allClipsData } = useClips(projectId)
  const allClips = (allClipsData || []) as Clip[]
  const { data: vfxShotsData } = useVfxShots(projectId)
  const allVfxShots = vfxShotsData?.shots || []

  // Get clips array from activity metadata (includes clip_id)
  const clipsWithIds = activityMetadata?.clips || []

  // Create a memoized map of nle_unique_id to clip_id from activity metadata
  const clipIdMap = useMemo(() => {
    const map = new Map<string, string>()
    clipsWithIds.forEach((clip: any) => {
      if (clip.nle_unique_id && clip.clip_id) {
        map.set(clip.nle_unique_id, clip.clip_id)
      }
    })
    return map
  }, [clipsWithIds])

  // Helper to normalize clip names by removing special characters (bullets, etc.)
  // Mirrors backend normalization during OTIO import
  const normalizeClipName = (name: string): string => {
    // Strip special characters that aren't alphanumeric, whitespace, hyphens, or periods
    // This matches the backend regex pattern: r'[^\w\s\-\.]'
    return name.replace(/[^\w\s\-\.]/g, '')
  }

  // Helper to get clip ID from NLE unique ID, clip name, tape name, or direct clip_id
  // Mirrors backend 5-point matching logic
  const getClipId = (nleUniqueId?: string, clipName?: string, tapeName?: string, clipId?: string): string | null => {
    // If backend already matched the clip, use that ID directly
    if (clipId) return clipId

    if (!nleUniqueId && !clipName) return null

    // First try the clipIdMap from activity metadata
    if (nleUniqueId) {
      const clipId = clipIdMap.get(nleUniqueId)
      if (clipId) return clipId
    }

    // Fall back to looking up the clip directly from allClips by nle_unique_id
    if (nleUniqueId) {
      // Try exact match first
      let clip = allClips.find(c => c.nle_unique_id === nleUniqueId)
      if (clip) return clip.id

      // Try without any prefix (file:, premiere_guid:, etc.)
      const withoutPrefix = nleUniqueId.replace(/^(file:|premiere_guid:|avid_umid:|premiere_link:)/, '')
      clip = allClips.find(c => c.nle_unique_id === withoutPrefix)
      if (clip) return clip.id

      // Try with "file:" prefix added if not present
      if (!nleUniqueId.includes(':')) {
        const withFilePrefix = `file:${nleUniqueId}`
        clip = allClips.find(c => c.nle_unique_id === withFilePrefix)
        if (clip) return clip.id
      }

      // Try matching by filename for file: clips
      if (nleUniqueId.startsWith('file:')) {
        const fileName = withoutPrefix
        clip = allClips.find(c => c.name === fileName)
        if (clip) return clip.id
      }
    }

    // Priority 2: Match by tape_name (source file name)
    // This is critical for clips that were renamed but have the same source file
    if (tapeName) {
      const normalizedTapeName = normalizeClipName(tapeName)
      const clip = allClips.find(c => c.tape_name && normalizeClipName(c.tape_name) === normalizedTapeName)
      if (clip) return clip.id
    }

    // Priority 3: Match by clip name + tape_name combination
    if (clipName && tapeName) {
      const normalizedName = normalizeClipName(clipName)
      const normalizedTape = normalizeClipName(tapeName)
      const clip = allClips.find(c =>
        normalizeClipName(c.name) === normalizedName &&
        c.tape_name &&
        normalizeClipName(c.tape_name) === normalizedTape
      )
      if (clip) return clip.id
    }

    // Priority 4: Match by clip name only (mirrors backend Priority 4: name-only matching)
    // Normalize both the search name and database names to handle special characters
    // (e.g., bullet • gets stripped during import, so "3-5b•" becomes "3-5b")
    if (clipName) {
      const normalizedSearchName = normalizeClipName(clipName)
      const clip = allClips.find(c => normalizeClipName(c.name) === normalizedSearchName)
      if (clip) return clip.id
    }

    // Clip doesn't exist in database - this is OK for nested sequences, adjustment layers, etc.
    // that may appear in comparisons but aren't imported as separate clip records
    return null
  }

  // Helper to get tags for a clip by NLE unique ID
  const getClipTags = (nleUniqueId?: string): string[] => {
    if (!nleUniqueId) return []
    const clip = allClips.find(c => c.nle_unique_id === nleUniqueId)
    return clip?.tags || []
  }

  // Helper to get tags for a VFX shot by name
  const getVfxShotTags = (shotName: string): string[] => {
    const shot = allVfxShots.find(s => s.shot_name === shotName)
    return shot?.tags || []
  }

  // Generic sort and group function
  const sortAndGroupItems = <T extends { clip_name?: string; marker_name?: string; track?: string; nle_unique_id?: string }>(
    items: T[],
    getItemName: (item: T) => string,
    getItemTags: (item: T) => string[]
  ): Array<{ tag: string | null; items: T[] }> => {
    if (sortBy === 'default') {
      return [{ tag: null, items }]
    }

    const sorted = [...items].sort((a, b) => {
      if (sortBy === 'tag') {
        const aTags = getItemTags(a)
        const bTags = getItemTags(b)
        const aTag = aTags[0] || 'Untagged'
        const bTag = bTags[0] || 'Untagged'
        return aTag.localeCompare(bTag)
      } else if (sortBy === 'name') {
        return getItemName(a).localeCompare(getItemName(b))
      } else if (sortBy === 'track') {
        const aTrack = a.track || ''
        const bTrack = b.track || ''
        return aTrack.localeCompare(bTrack)
      }
      return 0
    })

    if (sortBy === 'tag') {
      // Group by tag combination (all tags together)
      const grouped = new Map<string, T[]>()
      sorted.forEach(item => {
        const tags = getItemTags(item)
        // Create a key from all tags joined together
        const tagKey = tags.length > 0 ? tags.join(', ') : 'Untagged'
        if (!grouped.has(tagKey)) {
          grouped.set(tagKey, [])
        }
        grouped.get(tagKey)!.push(item)
      })

      // Convert to array and sort tag names alphabetically
      return Array.from(grouped.entries())
        .sort(([a], [b]) => {
          // Put "Untagged" last
          if (a === 'Untagged') return 1
          if (b === 'Untagged') return -1
          return a.localeCompare(b)
        })
        .map(([tag, items]) => ({ tag, items }))
    }

    return [{ tag: null, items: sorted }]
  }

  useEffect(() => {
    async function fetchDetails() {
      try {
        setLoading(true)
        const comparison = await getComparison(comparisonId)
        setActivityDetails(comparison.activity_details || null)
        setError(null)
      } catch (err) {
        console.error('Failed to fetch comparison details:', err)
        setError('Failed to load comparison details')
      } finally {
        setLoading(false)
      }
    }

    if (comparisonId) {
      fetchDetails()
    } else {
      setLoading(false)
    }
  }, [comparisonId])

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-3 bg-red-50 dark:bg-red-950/20 rounded border border-red-200 dark:border-red-800 flex items-center gap-2 text-red-600">
        <AlertTriangle className="h-4 w-4" />
        <span className="text-sm">{error}</span>
      </div>
    )
  }

  if (!activityDetails) {
    return (
      <div className="space-y-2">
        <div className="text-sm text-muted-foreground">
          No detailed activity information available
        </div>
        <div className="text-xs text-muted-foreground">
          Comparison ID: {comparisonId || 'Not provided'}
        </div>
      </div>
    )
  }

  const hasVfxWarnings = activityDetails.vfx_warnings.length > 0
  const hasChangedVfx = activityDetails.changed_vfx_shots.length > 0
  const hasNewVfx = activityDetails.new_vfx_shots.length > 0
  const hasOmitVfx = activityDetails.omit_vfx_shots.length > 0
  const hasChangedClips = activityDetails.changed_clips.length > 0
  const hasNewClips = activityDetails.new_clips.length > 0
  const hasRemovedClips = activityDetails.removed_clips.length > 0
  const hasMovedClips = activityDetails.moved_clips?.length > 0

  const hasAnyChanges = hasVfxWarnings || hasChangedVfx || hasNewVfx || hasOmitVfx || hasChangedClips || hasNewClips || hasRemovedClips || hasMovedClips

  if (!hasAnyChanges) {
    return (
      <div className="text-sm text-muted-foreground">
        No changes detected in this comparison
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Sort Filter */}
      <div className="flex items-center gap-2 pb-4 border-b">
        <label className="text-sm font-medium">Sort by:</label>
        <Select value={sortBy} onValueChange={(value: any) => setSortBy(value)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="default">Default</SelectItem>
            <SelectItem value="tag">Tag</SelectItem>
            <SelectItem value="name">Name</SelectItem>
            <SelectItem value="track">Track</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* VFX WARNINGS */}
      {hasVfxWarnings && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            <h4 className="font-semibold text-red-600 dark:text-red-400">
              VFX Warnings ({activityDetails.vfx_warnings.length})
            </h4>
          </div>
          <div className="space-y-2">
            {activityDetails.vfx_warnings.map((warning, idx) => (
              <div key={idx} className="p-3 bg-red-50 dark:bg-red-950/20 rounded border border-red-200 dark:border-red-800">
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-red-900 dark:text-red-100">{warning.marker_name}</p>
                    <Badge variant={warning.warning_type === 'needs_transcode' ? 'destructive' : 'secondary'}>
                      {warning.warning_type === 'needs_transcode' && 'Needs Transcode'}
                      {warning.warning_type === 'repositioned' && 'Repositioned'}
                      {warning.warning_type === 'removed' && 'Removed'}
                    </Badge>
                  </div>
                  <p className="text-sm text-red-700 dark:text-red-300">{warning.reason}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CHANGED VFX SHOTS */}
      {hasChangedVfx && (() => {
        const groupedShots = sortAndGroupItems(
          activityDetails.changed_vfx_shots,
          (shot) => shot.marker_name,
          (shot) => getVfxShotTags(shot.marker_name)
        )

        return (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-orange-500" />
              <h4 className="font-semibold text-orange-600 dark:text-orange-400">
                Changed VFX Shots ({activityDetails.changed_vfx_shots.length})
              </h4>
            </div>
            <div className="space-y-4">
              {groupedShots.map(({ tag, items }) => (
                <div key={tag || 'default'} className="space-y-2">
                  {tag && (
                    <h5 className="font-medium text-sm text-orange-700 dark:text-orange-300 border-b border-orange-200 dark:border-orange-800 pb-1">
                      {tag}
                    </h5>
                  )}
                  {items.map((shot, idx) => (
                    <div key={idx} className="p-3 bg-orange-50 dark:bg-orange-950/20 rounded border border-orange-200 dark:border-orange-800">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="font-medium">{shot.marker_name}</p>
                          {shot.track && (
                            <Badge variant="outline" className="text-xs">{shot.track}</Badge>
                          )}
                        </div>

                        {shot.duration_unchanged ? (
                          // Position shift only
                          <div className="text-sm space-y-1">
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <span>Frames:</span>
                              <span className="font-mono">
                                [{shot.old_frame_range?.[0]}-{shot.old_frame_range?.[1]}]
                              </span>
                              <ArrowRight className="h-3 w-3" />
                              <span className="font-mono">
                                [{shot.new_frame_range?.[0]}-{shot.new_frame_range?.[1]}]
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-muted-foreground text-xs">
                              <span>TC:</span>
                              <span className="font-mono">
                                {framesToTimecode(shot.old_frame_range?.[0] || 0)} - {framesToTimecode(shot.old_frame_range?.[1] || 0)}
                              </span>
                              <ArrowRight className="h-3 w-3" />
                              <span className="font-mono">
                                {framesToTimecode(shot.new_frame_range?.[0] || 0)} - {framesToTimecode(shot.new_frame_range?.[1] || 0)}
                              </span>
                            </div>
                            {shot.frame_shift !== undefined && (
                              <p className="text-xs">
                                Shifted {shot.frame_shift > 0 ? '+' : ''}{shot.frame_shift} frames
                              </p>
                            )}
                          </div>
                        ) : (
                          // Duration changed
                          <div className="text-sm space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="text-muted-foreground">Duration:</span>
                              <span className="font-mono">{shot.old_duration}f</span>
                              <ArrowRight className="h-3 w-3" />
                              <span className="font-mono">{shot.new_duration}f</span>
                              {shot.duration_delta !== undefined && (
                                <Badge variant={shot.duration_delta > 0 ? 'default' : 'destructive'} className="text-xs">
                                  {shot.duration_delta > 0 ? '+' : ''}{shot.duration_delta}f
                                </Badge>
                              )}
                            </div>
                          </div>
                        )}

                        {shot.affected_clips.length > 0 && (
                          <div className="text-xs text-muted-foreground">
                            Affects: {shot.affected_clips.join(', ')}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )
      })()}

      {/* NEW VFX SHOTS */}
      {hasNewVfx && (() => {
        const groupedShots = sortAndGroupItems(
          activityDetails.new_vfx_shots,
          (shot) => shot.marker_name,
          (shot) => getVfxShotTags(shot.marker_name)
        )

        return (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-green-500" />
              <h4 className="font-semibold text-green-600 dark:text-green-400">
                New VFX Shots ({activityDetails.new_vfx_shots.length})
              </h4>
            </div>
            <div className="space-y-4">
              {groupedShots.map(({ tag, items }) => (
                <div key={tag || 'default'} className="space-y-2">
                  {tag && (
                    <h5 className="font-medium text-sm text-green-700 dark:text-green-300 border-b border-green-200 dark:border-green-800 pb-1">
                      {tag}
                    </h5>
                  )}
                  {items.map((shot, idx) => (
                    <div key={idx} className="p-3 bg-green-50 dark:bg-green-950/20 rounded border border-green-200 dark:border-green-800">
                      <div className="space-y-1">
                        <div className="flex items-center justify-between">
                          <p className="font-medium">{shot.marker_name}</p>
                          <Badge variant="outline" className="text-xs">{shot.track}</Badge>
                        </div>
                        <div className="text-sm flex items-center gap-2 text-muted-foreground">
                          <span className="font-mono">[{shot.frame_range[0]}-{shot.frame_range[1]}]</span>
                          <span>•</span>
                          <span className="font-mono text-xs">{framesToTimecode(shot.frame_range[0])} - {framesToTimecode(shot.frame_range[1])}</span>
                          <span>•</span>
                          <span>{shot.duration} frames</span>
                        </div>
                        {shot.affected_clips.length > 0 && (
                          <div className="text-xs text-muted-foreground">
                            Clips: {shot.affected_clips.join(', ')}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )
      })()}

      {/* OMIT VFX SHOTS */}
      {hasOmitVfx && (() => {
        const groupedShots = sortAndGroupItems(
          activityDetails.omit_vfx_shots,
          (shot) => shot.marker_name,
          (shot) => getVfxShotTags(shot.marker_name)
        )

        return (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <X className="h-5 w-5 text-red-500" />
              <h4 className="font-semibold text-red-600 dark:text-red-400">
                OMIT VFX Shots ({activityDetails.omit_vfx_shots.length})
              </h4>
            </div>
            <div className="space-y-4">
              {groupedShots.map(({ tag, items }) => (
                <div key={tag || 'default'} className="space-y-2">
                  {tag && (
                    <h5 className="font-medium text-sm text-red-700 dark:text-red-300 border-b border-red-200 dark:border-red-800 pb-1">
                      {tag}
                    </h5>
                  )}
                  {items.map((shot, idx) => (
                    <div key={idx} className="p-3 bg-red-50 dark:bg-red-950/20 rounded border border-red-200 dark:border-red-800">
                      <div className="space-y-1">
                        <div className="flex items-center justify-between">
                          <p className="font-medium">{shot.marker_name}</p>
                          <Badge variant="outline" className="text-xs">{shot.track}</Badge>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          <span className="font-mono">[{shot.old_frame_range[0]}-{shot.old_frame_range[1]}]</span>
                          <span className="ml-2">•</span>
                          <span className="font-mono text-xs ml-2">{framesToTimecode(shot.old_frame_range[0])} - {framesToTimecode(shot.old_frame_range[1])}</span>
                          <span className="ml-2">•</span>
                          <span className="ml-2">{shot.old_duration} frames</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )
      })()}

      {/* CHANGED CLIPS */}
      {hasChangedClips && (() => {
        const groupedClips = sortAndGroupItems(
          activityDetails.changed_clips,
          (clip) => clip.clip_name,
          (clip) => getClipTags(clip.nle_unique_id)
        )

        return (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <TrendingDown className="h-5 w-5 text-blue-500" />
              <h4 className="font-semibold text-blue-600 dark:text-blue-400">
                Changed Clips ({activityDetails.changed_clips.length})
              </h4>
            </div>
            <div className="space-y-4">
              {groupedClips.map(({ tag, items }) => (
                <div key={tag || 'default'} className="space-y-2">
                  {tag && (
                    <h5 className="font-medium text-sm text-blue-700 dark:text-blue-300 border-b border-blue-200 dark:border-blue-800 pb-1">
                      {tag}
                    </h5>
                  )}
                  {items.map((clip, idx) => {
                    const clipId = getClipId(clip.nle_unique_id, clip.clip_name, clip.tape_name, clip.clip_id)
                    return (
                      <div key={idx} className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded border border-blue-200 dark:border-blue-800">
                        <div className="space-y-2">
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-medium text-sm flex-1">{clip.clip_name}</p>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">{clip.track}</Badge>
                              <Badge variant="secondary" className="text-xs capitalize">{clip.change_type}</Badge>
                              {clipId && onViewClip && (
                                <button
                                  onClick={() => onViewClip(clipId)}
                                  className="text-xs px-2 py-0.5 rounded hover:opacity-80 transition-opacity bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 whitespace-nowrap"
                                >
                                  View Clip →
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Duration change for trim/extend */}
                          {clip.duration_change !== undefined && clip.duration_change !== 0 && (
                            <div className="text-xs space-y-0.5">
                              <div className={clip.duration_change < 0 ? 'text-orange-700 dark:text-orange-400' : 'text-green-700 dark:text-green-400'}>
                                <span className="font-medium">
                                  {clip.change_type === 'trimmed' ? 'Trimmed' : 'Extended'} by {Math.abs(clip.duration_change)} frames
                                </span>
                                <span className="ml-2 text-muted-foreground">
                                  ({clip.old_duration} → {clip.new_duration} frames)
                                </span>
                              </div>
                            </div>
                          )}

                          {/* Position change if applicable */}
                          {clip.old_position && clip.new_position && (
                            <div className="text-xs text-muted-foreground space-y-0.5">
                              <div>
                                <span>Timeline: </span>
                                <span className="font-mono">[{clip.old_position[0]}-{clip.old_position[1]}]</span>
                                <ArrowRight className="h-3 w-3 inline mx-1" />
                                <span className="font-mono">[{clip.new_position[0]}-{clip.new_position[1]}]</span>
                                {clip.frame_shift !== undefined && clip.frame_shift !== 0 && !clip.duration_change && (
                                  <span className="ml-2 text-xs">
                                    ({clip.frame_shift > 0 ? '+' : ''}{clip.frame_shift} frames)
                                  </span>
                                )}
                              </div>
                              <div className="font-mono opacity-75">
                                TC: {framesToTimecode(clip.old_position[0])} - {framesToTimecode(clip.old_position[1])}
                                <ArrowRight className="h-3 w-3 inline mx-1" />
                                {framesToTimecode(clip.new_position[0])} - {framesToTimecode(clip.new_position[1])}
                              </div>
                            </div>
                          )}

                          {/* Visible range change for occlusion */}
                          {clip.change_type === 'occlusion' && clip.old_visible_range && clip.new_visible_range && (
                            <div className="text-xs text-muted-foreground space-y-0.5">
                              <div>
                                <span>Visible: </span>
                                {clip.old_visible_range.map((range, i) => (
                                  <span key={i} className="font-mono">
                                    {i > 0 && ', '}[{range[0]}-{range[1]}]
                                  </span>
                                ))}
                                <ArrowRight className="h-3 w-3 inline mx-1" />
                                {clip.new_visible_range.map((range, i) => (
                                  <span key={i} className="font-mono">
                                    {i > 0 && ', '}[{range[0]}-{range[1]}]
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {clip.affects_vfx.length > 0 && (
                            <div className="p-2 bg-orange-50 dark:bg-orange-950/20 rounded border border-orange-200 dark:border-orange-800">
                              <p className="text-xs">
                                <span className="font-medium">Affects VFX:</span> {clip.affects_vfx.join(', ')}
                              </p>
                            </div>
                          )}

                          {clip.is_completely_hidden && (
                            <div className="p-2 bg-amber-50 dark:bg-amber-950/20 rounded border border-amber-300 dark:border-amber-700">
                              <p className="text-xs text-amber-800 dark:text-amber-200">
                                <span className="font-medium">⚠️ Completely Hidden:</span> No frames visible in output.
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              ))}
            </div>
          </div>
        )
      })()}

      {/* MOVED CLIPS */}
      {hasMovedClips && (() => {
        const groupedClips = sortAndGroupItems(
          activityDetails.moved_clips,
          (clip) => clip.clip_name,
          (clip) => getClipTags(clip.nle_unique_id)
        )

        return (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <ArrowRight className="h-5 w-5 text-purple-500" />
              <h4 className="font-semibold text-purple-600 dark:text-purple-400">
                Moved Clips ({activityDetails.moved_clips.length})
              </h4>
            </div>
            <div className="space-y-4">
              {groupedClips.map(({ tag, items }) => (
                <div key={tag || 'default'} className="space-y-2">
                  {tag && (
                    <h5 className="font-medium text-sm text-purple-700 dark:text-purple-300 border-b border-purple-200 dark:border-purple-800 pb-1">
                      {tag}
                    </h5>
                  )}
                  {items.map((clip, idx) => {
                    const clipId = getClipId(clip.nle_unique_id, clip.clip_name, clip.tape_name, clip.clip_id)
                    return (
                      <div key={idx} className="p-3 bg-purple-50 dark:bg-purple-950/20 rounded border border-purple-200 dark:border-purple-800">
                        <div className="space-y-2">
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-medium text-sm flex-1">{clip.clip_name}</p>
                            <div className="flex items-center gap-2">
                              {clipId && onViewClip && (
                                <button
                                  onClick={() => onViewClip(clipId)}
                                  className="text-xs px-2 py-0.5 rounded hover:opacity-80 transition-opacity bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 whitespace-nowrap"
                                >
                                  View Clip →
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Track change */}
                          <div className="flex items-center gap-2 text-sm">
                            <Badge variant="outline" className="text-xs">{clip.old_track}</Badge>
                            <ArrowRight className="h-3 w-3 text-purple-500" />
                            <Badge variant="outline" className="text-xs">{clip.new_track}</Badge>
                          </div>

                          {/* Frame range and duration */}
                          <div className="text-xs text-muted-foreground space-y-0.5">
                            <div>
                              <span className="font-mono">[{clip.frame_range[0]}-{clip.frame_range[1]}]</span>
                              <span className="ml-2">•</span>
                              <span className="ml-2">{clip.duration} frames</span>
                            </div>
                            <div className="font-mono text-xs opacity-75">
                              TC: {framesToTimecode(clip.frame_range[0])} - {framesToTimecode(clip.frame_range[1])}
                            </div>
                          </div>

                          {/* Position change if applicable */}
                          {clip.position_changed && clip.old_position && clip.new_position && (
                            <div className="text-xs text-muted-foreground space-y-0.5">
                              <div>
                                <span>Frames: </span>
                                <span className="font-mono">[{clip.old_position[0]}-{clip.old_position[1]}]</span>
                                <ArrowRight className="h-3 w-3 inline mx-1" />
                                <span className="font-mono">[{clip.new_position[0]}-{clip.new_position[1]}]</span>
                              </div>
                              <div className="font-mono opacity-75">
                                TC: {framesToTimecode(clip.old_position[0])} - {framesToTimecode(clip.old_position[1])}
                                <ArrowRight className="h-3 w-3 inline mx-1" />
                                {framesToTimecode(clip.new_position[0])} - {framesToTimecode(clip.new_position[1])}
                              </div>
                            </div>
                          )}

                          {/* VFX impact */}
                          {clip.affects_vfx.length > 0 && (
                            <div className="p-2 bg-orange-50 dark:bg-orange-950/20 rounded border border-orange-200 dark:border-orange-800">
                              <p className="text-xs">
                                <span className="font-medium">Affects VFX:</span> {clip.affects_vfx.join(', ')}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              ))}
            </div>
          </div>
        )
      })()}

      {/* NEW CLIPS */}
      {hasNewClips && (() => {
        const groupedClips = sortAndGroupItems(
          activityDetails.new_clips,
          (clip) => clip.clip_name,
          (clip) => getClipTags(clip.nle_unique_id)
        )

        return (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-green-500" />
              <h4 className="font-semibold text-green-600 dark:text-green-400">
                New Clips ({activityDetails.new_clips.length})
              </h4>
            </div>
            <div className="space-y-4">
              {groupedClips.map(({ tag, items }) => (
                <div key={tag || 'default'} className="space-y-2">
                  {tag && (
                    <h5 className="font-medium text-sm text-green-700 dark:text-green-300 border-b border-green-200 dark:border-green-800 pb-1">
                      {tag}
                    </h5>
                  )}
                  {items.map((clip, idx) => {
                    const clipId = getClipId(clip.nle_unique_id, clip.clip_name, clip.tape_name, clip.clip_id)
                    return (
                      <div key={idx} className="p-3 bg-green-50 dark:bg-green-950/20 rounded border border-green-200 dark:border-green-800">
                        <div className="space-y-2">
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-medium text-sm flex-1">{clip.clip_name}</p>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">{clip.track}</Badge>
                              {clipId && onViewClip && (
                                <button
                                  onClick={() => onViewClip(clipId)}
                                  className="text-xs px-2 py-0.5 rounded hover:opacity-80 transition-opacity bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 whitespace-nowrap"
                                >
                                  View Clip →
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="text-xs text-muted-foreground space-y-0.5">
                            <div>
                              <span className="font-mono">[{clip.frame_range[0]}-{clip.frame_range[1]}]</span>
                              <span className="ml-2">•</span>
                              <span className="ml-2">{clip.duration} frames</span>
                            </div>
                            <div className="font-mono text-xs opacity-75">
                              TC: {framesToTimecode(clip.frame_range[0])} - {framesToTimecode(clip.frame_range[1])}
                            </div>
                          </div>

                          {clip.impact === 'occludes_vfx' && clip.affects_vfx.length > 0 && (
                            <div className="p-2 bg-red-50 dark:bg-red-950/20 rounded border border-red-200 dark:border-red-800">
                              <p className="text-xs text-red-700 dark:text-red-300">
                                <span className="font-medium">Occludes VFX:</span> {clip.affects_vfx.join(', ')}
                              </p>
                            </div>
                          )}

                          {clip.is_completely_hidden && (
                            <div className="p-2 bg-amber-50 dark:bg-amber-950/20 rounded border border-amber-300 dark:border-amber-700">
                              <p className="text-xs text-amber-800 dark:text-amber-200">
                                <span className="font-medium">⚠️ Completely Hidden:</span> No frames visible in output.
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              ))}
            </div>
          </div>
        )
      })()}

      {/* REMOVED CLIPS */}
      {hasRemovedClips && (() => {
        const groupedClips = sortAndGroupItems(
          activityDetails.removed_clips,
          (clip) => clip.clip_name,
          (clip) => getClipTags(clip.nle_unique_id)
        )

        return (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Minus className="h-5 w-5 text-red-500" />
              <h4 className="font-semibold text-red-600 dark:text-red-400">
                Removed Clips ({activityDetails.removed_clips.length})
              </h4>
            </div>
            <div className="space-y-4">
              {groupedClips.map(({ tag, items }) => (
                <div key={tag || 'default'} className="space-y-2">
                  {tag && (
                    <h5 className="font-medium text-sm text-red-700 dark:text-red-300 border-b border-red-200 dark:border-red-800 pb-1">
                      {tag}
                    </h5>
                  )}
                  {items.map((clip, idx) => {
                    const clipId = getClipId(clip.nle_unique_id, clip.clip_name, clip.tape_name, clip.clip_id)
                    return (
                      <div key={idx} className="p-3 bg-red-50 dark:bg-red-950/20 rounded border border-red-200 dark:border-red-800">
                        <div className="space-y-1">
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-medium text-sm flex-1">{clip.clip_name}</p>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">{clip.track}</Badge>
                              {clipId && onViewClip && (
                                <button
                                  onClick={() => onViewClip(clipId)}
                                  className="text-xs px-2 py-0.5 rounded hover:opacity-80 transition-opacity bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 whitespace-nowrap"
                                >
                                  View Clip →
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="text-xs text-muted-foreground space-y-0.5">
                            <div>
                              <span className="font-mono">[{clip.old_frame_range[0]}-{clip.old_frame_range[1]}]</span>
                              <span className="ml-2">•</span>
                              <span className="ml-2">{clip.old_duration} frames</span>
                            </div>
                            <div className="font-mono text-xs opacity-75">
                              TC: {framesToTimecode(clip.old_frame_range[0])} - {framesToTimecode(clip.old_frame_range[1])}
                            </div>
                          </div>

                          {clip.is_completely_hidden && (
                            <div className="p-2 bg-amber-50 dark:bg-amber-950/20 rounded border border-amber-300 dark:border-amber-700 mt-2">
                              <p className="text-xs text-amber-800 dark:text-amber-200">
                                <span className="font-medium">⚠️ Was Completely Hidden:</span> No frames were visible in output.
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              ))}
            </div>
          </div>
        )
      })()}
    </div>
  )
}
